import { useReducer, useCallback, useRef, useEffect } from 'react';
import {
  extractWords,
  WordQueue,
  createEmptyGrid,
  createPiece,
  getRandomTetromino,
  rotatePiece,
  isValidPosition,
  lockPiece,
  clearLines,
  calculateScore,
  calculateLevel,
  calculateDropInterval,
} from '../../../utils/tetris-utils';

const initialState = {
  status: 'idle', // idle | playing | paused | gameover
  grid: createEmptyGrid(),
  currentPiece: null,
  nextPiece: null,
  score: 0,
  level: 1,
  lines: 0,
  clearedWords: [],
  dropInterval: 1000,
};

const actionTypes = {
  START: 'START',
  PAUSE: 'PAUSE',
  RESUME: 'RESUME',
  MOVE_LEFT: 'MOVE_LEFT',
  MOVE_RIGHT: 'MOVE_RIGHT',
  MOVE_DOWN: 'MOVE_DOWN',
  ROTATE: 'ROTATE',
  HARD_DROP: 'HARD_DROP',
  RESET: 'RESET',
};

const getNextPiece = (wordQueue) => {
  const type = getRandomTetromino();
  const word = wordQueue.current?.getNext() || '';
  return createPiece(type, word);
};

const handleLock = (state, wordQueue) => {
  const { currentPiece, grid, lines: totalLines, level } = state;
  if (!currentPiece) return state;

  // Lock current piece
  let newGrid = lockPiece(grid, currentPiece);

  // Clear lines
  const { grid: clearedGrid, linesCleared, clearedWords } = clearLines(newGrid);
  newGrid = clearedGrid;

  const newTotalLines = totalLines + linesCleared;
  const newLevel = calculateLevel(newTotalLines);
  const scoreAdd = calculateScore(linesCleared, level);

  // Get next piece
  const nextPiece = getNextPiece(wordQueue);

  // Check game over
  if (!isValidPosition(newGrid, nextPiece)) {
    return {
      ...state,
      status: 'gameover',
      grid: newGrid,
      currentPiece: null,
      score: state.score + scoreAdd,
      lines: newTotalLines,
      clearedWords: [...state.clearedWords, ...clearedWords],
    };
  }

  return {
    ...state,
    grid: newGrid,
    currentPiece: nextPiece,
    nextPiece: getNextPiece(wordQueue),
    score: state.score + scoreAdd,
    level: newLevel,
    lines: newTotalLines,
    clearedWords: [...state.clearedWords, ...clearedWords],
    dropInterval: calculateDropInterval(newLevel),
  };
};

const createReducer = (wordQueue) => (state, action) => {
  switch (action.type) {
  case actionTypes.START: {
    wordQueue.current?.reset();
    const firstPiece = getNextPiece(wordQueue);
    const nextPiece = getNextPiece(wordQueue);
    return {
      ...initialState,
      status: 'playing',
      currentPiece: firstPiece,
      nextPiece,
    };
  }

  case actionTypes.PAUSE:
    return state.status === 'playing' ? { ...state, status: 'paused' } : state;

  case actionTypes.RESUME:
    return state.status === 'paused' ? { ...state, status: 'playing' } : state;

  case actionTypes.MOVE_LEFT: {
    if (state.status !== 'playing' || !state.currentPiece) return state;
    if (isValidPosition(state.grid, state.currentPiece, -1, 0)) {
      return {
        ...state,
        currentPiece: { ...state.currentPiece, x: state.currentPiece.x - 1 },
      };
    }
    return state;
  }

  case actionTypes.MOVE_RIGHT: {
    if (state.status !== 'playing' || !state.currentPiece) return state;
    if (isValidPosition(state.grid, state.currentPiece, 1, 0)) {
      return {
        ...state,
        currentPiece: { ...state.currentPiece, x: state.currentPiece.x + 1 },
      };
    }
    return state;
  }

  case actionTypes.MOVE_DOWN: {
    if (state.status !== 'playing' || !state.currentPiece) return state;
    if (isValidPosition(state.grid, state.currentPiece, 0, 1)) {
      return {
        ...state,
        currentPiece: { ...state.currentPiece, y: state.currentPiece.y + 1 },
      };
    }
    return handleLock(state, wordQueue);
  }

  case actionTypes.ROTATE: {
    if (state.status !== 'playing' || !state.currentPiece) return state;
    const rotated = rotatePiece(state.currentPiece);
    // Try rotation, with wall kicks
    const kicks = [0, -1, 1, -2, 2];
    for (const kick of kicks) {
      if (isValidPosition(state.grid, { ...rotated, x: rotated.x + kick })) {
        return {
          ...state,
          currentPiece: { ...rotated, x: rotated.x + kick },
        };
      }
    }
    return state;
  }

  case actionTypes.HARD_DROP: {
    if (state.status !== 'playing' || !state.currentPiece) return state;
    let dropY = state.currentPiece.y;
    while (isValidPosition(state.grid, state.currentPiece, 0, dropY - state.currentPiece.y + 1)) {
      dropY++;
    }
    const droppedPiece = { ...state.currentPiece, y: dropY };
    return handleLock({ ...state, currentPiece: droppedPiece }, wordQueue);
  }

  case actionTypes.RESET:
    wordQueue.current?.reset();
    return { ...initialState };

  default:
    return state;
  }
};

const useTetrisGame = (text) => {
  const wordQueueRef = useRef(null);
  const reducerRef = useRef(null);
  const dropTimerRef = useRef(null);

  // Initialize word queue
  useEffect(() => {
    if (text) {
      const words = extractWords(text);
      wordQueueRef.current = new WordQueue(words);
    }
  }, [text]);

  if (!reducerRef.current) {
    reducerRef.current = createReducer(wordQueueRef);
  }

  const [state, dispatch] = useReducer(reducerRef.current, initialState);

  // Auto drop timer
  useEffect(() => {
    if (state.status === 'playing') {
      dropTimerRef.current = setInterval(() => {
        dispatch({ type: actionTypes.MOVE_DOWN });
      }, state.dropInterval);
    } else {
      if (dropTimerRef.current) {
        clearInterval(dropTimerRef.current);
        dropTimerRef.current = null;
      }
    }
    return () => {
      if (dropTimerRef.current) clearInterval(dropTimerRef.current);
    };
  }, [state.status, state.dropInterval]);

  const startGame = useCallback(() => dispatch({ type: actionTypes.START }), []);
  const pauseGame = useCallback(() => dispatch({ type: actionTypes.PAUSE }), []);
  const resumeGame = useCallback(() => dispatch({ type: actionTypes.RESUME }), []);
  const resetGame = useCallback(() => {
    if (dropTimerRef.current) clearInterval(dropTimerRef.current);
    dispatch({ type: actionTypes.RESET });
  }, []);
  const moveLeft = useCallback(() => dispatch({ type: actionTypes.MOVE_LEFT }), []);
  const moveRight = useCallback(() => dispatch({ type: actionTypes.MOVE_RIGHT }), []);
  const moveDown = useCallback(() => dispatch({ type: actionTypes.MOVE_DOWN }), []);
  const rotate = useCallback(() => dispatch({ type: actionTypes.ROTATE }), []);
  const hardDrop = useCallback(() => dispatch({ type: actionTypes.HARD_DROP }), []);

  return {
    gameState: state,
    startGame,
    pauseGame,
    resumeGame,
    resetGame,
    moveLeft,
    moveRight,
    moveDown,
    rotate,
    hardDrop,
  };
};

export default useTetrisGame;
